import { useEffect, useState } from "react";
import {
  Heading,
  Button,
  Icon,
  TextField,
} from "@elaniintc/goes-design-system";
import type { SlotType } from "@/types/entities/templates";
import { AddModal } from "@/pages/portal/faqs/components/add-modal";
import SelectField from "@/components/common/select";
import { cn } from "@/utils/theme";

interface SlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (slotName: string, type: SlotType) => void;
  slotToEdit?: { name: string; type: SlotType } | null;
  onDelete?: (slotName: string) => void;
  onUpdate?: (oldName: string, newName: string, newType: SlotType) => void;
}

const slotTypes = [
  {
    label: "Texto (String)",
    value: "string",
    icon: <Icon type="far" name="text" />,
    iconPosition: "left",
  },
  {
    label: "Número (Number)",
    value: "number",
    icon: <Icon type="far" name="hashtag" />,
    iconPosition: "left",
  },
  {
    label: "Fecha (Date)",
    value: "date",
    icon: <Icon type="far" name="calendar" />,
    iconPosition: "left",
  },
];

export default function SlotModal({
  isOpen,
  onClose,
  onInsert,
  slotToEdit,
  onDelete,
  onUpdate,
}: SlotModalProps) {
  const isEditMode = !!slotToEdit;
  const [slotName, setSlotName] = useState("");
  const [slotType, setSlotType] = useState<SlotType>("string");

  useEffect(() => {
    if (slotToEdit) {
      setSlotName(slotToEdit.name);
      setSlotType(slotToEdit.type);
    } else {
      setSlotName("");
      setSlotType("string");
    }
  }, [slotToEdit, isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!slotName.trim() || !slotType) return;

    const normalizedName = slotName.trim().toLowerCase().replace(/\s+/g, "_");

    if (isEditMode && slotToEdit) {
      if (onUpdate) {
        onUpdate(slotToEdit.name, normalizedName, slotType);
      }
    } else {
      onInsert(normalizedName, slotType);
    }

    setSlotName("");
    setSlotType("string");
    onClose();
  };

  const handleDelete = () => {
    if (slotToEdit && onDelete) {
      onDelete(slotToEdit.name);
      onClose();
    }
  };

  return (
    <AddModal
      onClose={onClose}
      isOpen={isOpen}
      modalContentClassName="max-w-[520px]"
      header={
        <div className="flex items-center justify-between px-8 py-3 border-b border-neutral-silent">
          <Heading variant="h5" className="!mb-0">
            {isEditMode ? "Editar variable" : "Crear variable"}
          </Heading>
          <Button
            type="button"
            variant="quiet"
            aria-label="Cerrar"
            onClick={onClose}
            className="w-8 h-8"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div
          className={cn(
            "border-t border-neutral  w-full flex justify-between py-3 gap-3 px-8",
          )}
        >
          {isEditMode && onDelete && (
            <Button
              variant="danger-tertiary"
              size="lg"
              type="button"
              onClick={handleDelete}
              className="min-w-[120px]"
            >
              Eliminar
            </Button>
          )}

          <div className={cn("flex gap-3", isEditMode ? "" : "w-1/2")}>
            <Button
              variant="tertiary"
              size="lg"
              type="button"
              onClick={onClose}
              className={cn("cursor-pointer", isEditMode ? "" : "w-full")}
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              size="lg"
              type="submit"
              form="slot-form"
              disabled={!slotName}
              className={cn("cursor-pointer", isEditMode ? "" : "w-full")}
            >
              {isEditMode ? "Aceptar" : "Agregar"}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id="slot-form"
        onSubmit={handleSubmit}
        data-testid="slot-form"
        className="p-modal-md space-y-4"
      >
        <TextField
          name="slotName"
          value={slotName}
          onChange={(e) => setSlotName(e.target.value)}
          label={"Nombre de la variable"}
          required
          placeholder="Escribe el nombre de la variable"
        />

        <SelectField
          name={"slotType"}
          label={"Tipo de variable"}
          placeholder={"Selecciona un tipo de variable..."}
          value={slotType}
          options={slotTypes}
          onChange={(value) => setSlotType(value as SlotType)}
          required
        />
      </form>
    </AddModal>
  );
}
